import ErrorBoundary from '../../../containers/error_boundary'

// CS stands for Connection Status
export enum CS {
  DISCONNECTED,
  JOINING,
  CALL_CONNECTED,
  ROOM_CONNECTED,
  ROOM_RECONNECTING,
  LEAVING_CALL,
  JOIN_ROOM_ERROR,
}

export const TRANSIENT_STATES = [CS.JOINING, CS.LEAVING_CALL]

// CE stands for Connection Event
// Events are decoupled from CS state, but are mostly one-to-one
export enum CE {
  DISCONNECTED,
  JOINING,
  CALL_CONNECTED,
  ROOM_CONNECTED,
  ROOM_RECONNECTING,
  LEAVING_CALL,
  ROOM_DISCONNECTED,
  JOIN_ROOM_ERROR,
  DISCONNECTED_EXTERNALLY,
}

// For a given state (CS), which events (CE) are valid
const VALID_EVENTS = {
  [CS.DISCONNECTED]: [CE.JOINING],
  [CS.JOINING]: [
    CE.CALL_CONNECTED,
    CE.ROOM_CONNECTED,
    CE.DISCONNECTED,
    CE.JOIN_ROOM_ERROR,
    CE.DISCONNECTED_EXTERNALLY,
  ],
  [CS.CALL_CONNECTED]: [
    CE.LEAVING_CALL,
    CE.JOINING,
    CE.DISCONNECTED_EXTERNALLY,
  ],
  [CS.ROOM_CONNECTED]: [
    CE.LEAVING_CALL,
    CE.ROOM_RECONNECTING,
    CE.ROOM_DISCONNECTED,
    CE.DISCONNECTED_EXTERNALLY,
  ],
  [CS.ROOM_RECONNECTING]: [
    CE.ROOM_CONNECTED,
    CE.ROOM_DISCONNECTED,
    CE.LEAVING_CALL,
  ],
  [CS.LEAVING_CALL]: [
    CE.DISCONNECTED,
    CE.ROOM_DISCONNECTED,
    CE.ROOM_CONNECTED,
    CE.CALL_CONNECTED,
  ],
  [CS.JOIN_ROOM_ERROR]: [
    CE.LEAVING_CALL,
    CE.JOINING,
    CE.DISCONNECTED_EXTERNALLY,
    CE.CALL_CONNECTED,
  ],
}

type ConnectionStatusState = {
  current: CS
  previous: CS
  leavingRoom: boolean
  leavingCall: boolean
}

type ConnectionStatusStateFragment = {
  current?: CS
  previous?: CS
  leavingRoom?: boolean
  leavingCall?: boolean
}

export const initialConnectionStatusState: ConnectionStatusState = {
  current: CS.DISCONNECTED,
  previous: CS.DISCONNECTED,
  leavingRoom: false,
  leavingCall: false,
}

export const connectionStatusReducer = (
  state: ConnectionStatusState,
  event: CE,
): ConnectionStatusState => {
  const { current, leavingRoom, leavingCall } = state
  const validEvent = VALID_EVENTS[current].includes(event)
  if (!validEvent) {
    ErrorBoundary.logErrorToMyService(
      new Error(
        `invalid connection event. state: ${CS[current]}, event: ${CE[event]}`,
      ),
    )
    return state
  }
  let newState = initializeFromEvent(event)

  switch (event) {
    case CE.LEAVING_CALL:
      if (current === CS.ROOM_CONNECTED || current === CS.ROOM_RECONNECTING) {
        newState = {
          ...newState,
          leavingRoom: true,
          leavingCall: true,
        }
      } else {
        newState = { ...newState, leavingCall: true }
      }
      break
    case CE.ROOM_DISCONNECTED:
      if (leavingCall) {
        newState = {
          leavingRoom: false,
        }
      } else if (leavingRoom) {
        newState = {
          current: CS.DISCONNECTED,
          leavingRoom: false,
        }
      } else {
        newState = { current: CS.CALL_CONNECTED }
      }
      break
    case CE.DISCONNECTED:
      if (leavingRoom) {
        newState = { leavingCall: false }
      } else if (leavingCall) {
        newState = {
          ...newState,
          leavingCall: false,
        }
      }
      break
    case CE.DISCONNECTED_EXTERNALLY:
      newState = { current: CS.DISCONNECTED }
  }

  return {
    ...state,
    ...newState,
    ...(newState.current !== state.current && { previous: state.current }),
  }
}

const initializeFromEvent = (event: CE): ConnectionStatusStateFragment => {
  // eslint-disable-next-line
  // @ts-ignore
  const eventKey = CE[event]
  // eslint-disable-next-line
  // @ts-ignore
  const status = Object.values(CS).includes(eventKey) && CS[eventKey]
  if (status || status === 0) {
    return { current: status }
  } else {
    return {}
  }
}
